import { signIn, signOut, useSession } from 'next-auth/react';
import { useRouter } from 'next/router';
import { useEffect, useState } from 'react';
import { useForm } from 'react-hook-form';
import { useDispatch, useSelector } from 'react-redux';

import { FIELD_TYPES, STRINGS } from '../../../constants';
import { deleteCar, fetchCars } from '../../../services/car';
import { fetchUserProfile } from '../../../services/profile';
import { createVisitByWorkshop } from '../../../services/visits';
import { getScannedCars } from '../../../store/actions';
import CarsScreen from '../../scenes/car/CarsScreen';

const CarsScreenContainer = () => {
  const { data: session, status } = useSession();
  const cars = useSelector((state) => state.scannedCars);
  const [isDetailsLoading, setIsDetailsLoading] = useState(false);
  const [isLoading, setIsLoading] = useState(true);
  const [isModalLoading, setIsModalLoading] = useState(false);
  const [isRefreshing, setIsRefreshing] = useState(false);
  const [isReady, setIsReady] = useState(false);
  const [error, setError] = useState(null);
  const [profile, setProfile] = useState(null);
  const [car, setCar] = useState(null);
  const [modalVisible, setModalVisible] = useState(false);
  const [visitDate, setVisitDate] = useState(null);
  const {
    register,
    handleSubmit,
    formState: { errors },
  } = useForm({ reValidateMode: 'onSubmit' });
  const dispatch = useDispatch();
  const router = useRouter();

  const loadGarage = async () => {
    setIsLoading(true);
    try {
      await refreshCars();
    } catch (err) {
      setError(err);
    }
    setIsLoading(false);
  };

  useEffect(() => {
    if (session?.error === 'RefreshAccessTokenError' || status === 'unauthenticated') {
      signIn(); // Force sign in to hopefully resolve error
    }
    if (session?.user && status === 'authenticated') {
      fetchUserProfileHandler(session?.user);
      loadGarage();
    }
    setModalVisible(false);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [session?.user?.accessToken, session?.error, status]);

  const updateDate = (date) => {
    setVisitDate(date);
    setIsReady(true);
  };

  const onRefresh = async () => {
    setIsRefreshing(true);
    await refreshCars();
    setIsRefreshing(false);
  };

  const onRefreshWithLoadingIndicator = async () => {
    setIsLoading(true);
    await refreshCars();
    setIsLoading(false);
  };

  const fetchUserProfileHandler = async () => {
    await fetchUserProfile(session?.user?.profileId, setProfile);
  };

  const saveCars = (data) => {
    dispatch(getScannedCars(session?.user?.profileId, data));
  };

  const deleteCarHandler = async (ownershipId) => {
    setIsDetailsLoading(true);
    await deleteCar(
      session?.user?.profileId,
      ownershipId,
      onRefreshWithLoadingIndicator,
      setError,
      logoutHandler,
    );
    setIsDetailsLoading(false);
  };

  const refreshCars = async () => {
    await fetchCars(session?.user?.profileId, setError, saveCars, logoutHandler);
  };

  const visitNavigator = (responseId) => {
    const url = `/visits/${car.car.make}/${car.car.model}/${responseId || car.visitId}`;
    router.push(url);
  };

  const carActionHandler = () => {
    return setModalVisible(true);
  };

  const clearFormSuccess = () => {
    setModalVisible(false);
    setVisitDate(null);
    setIsModalLoading(false);
  };

  const clearFormError = (value) => {
    if (value) {
      setError(value);
      setIsModalLoading(false);
    } else {
      setModalVisible(false);
      setIsModalLoading(false);
      setVisitDate(null);
    }
  };

  const createVisitHandler = handleSubmit(async (message) => {
    setIsModalLoading(true);
    const data = {
      carOwnershipId: car.car.ownershipId,
      workshopOwnershipId: car.workshopOwnershipId,
      scannedCarId: car.id,
      message: message.message,
      selectedTime: visitDate,
    };
    await createVisitByWorkshop(
      data,
      clearFormError,
      clearFormSuccess,
      visitNavigator,
      logoutHandler,
    );
  });

  const historyNavigator = (carId, make, model) => {
    router.push({
      pathname: '/history',
      query: { carId, make, model },
    });
  };

  const logoutHandler = async () => {
    await signOut();
  };

  const modalFields = [
    {
      type: FIELD_TYPES.INPUT_TEXT_AREA,
      name: 'message',
      value: null,
      action: null,
      label: STRINGS.visit.messageFromWorkshopLabel,
      showLabel: true,
      fieldError: errors.message,
      register,
      validation: {
        required: { value: false, message: STRINGS.VALIDATORS.EMPTY_FIELD },
        maxLength: { value: 512, message: STRINGS.VALIDATORS.INVALID_LENGTH },
      },
      inputProps: {
        autoCapitalize: 'sentences',
        multiline: true,
      },
    },
    {
      type: FIELD_TYPES.DATE_PICKER,
      name: 'date',
      value: visitDate,
      setValue: updateDate,
      label: STRINGS.visit.selectVisitTime,
      showLabel: true,
      validation: null,
      inputProps: null,
    },
  ];

  return (
    <CarsScreen
      profile={profile}
      logoutHandler={logoutHandler}
      session={session}
      status={status}
      cars={cars?.cars}
      isWorkshopOwnership={cars?.isWorkshopOwnership}
      car={car}
      setCar={setCar}
      modalFields={modalFields}
      modalVisible={modalVisible}
      setModalVisible={setModalVisible}
      carActionHandler={carActionHandler}
      createVisitHandler={createVisitHandler}
      deleteCarHandler={deleteCarHandler}
      isReady={isReady}
      isOwner={false}
      isLoading={isLoading}
      isRefreshing={isRefreshing}
      isDetailsLoading={isDetailsLoading}
      isModalLoading={isModalLoading}
      setIsModalLoading={setIsModalLoading}
      historyNavigator={historyNavigator}
      onRefresh={onRefresh}
      onRefreshWithLoadingIndicator={onRefreshWithLoadingIndicator}
      error={error}
    />
  );
};

export default CarsScreenContainer;
